import type { Direction, Position, MovementStyle } from '../types';

/**
 * BLSAnimationEngine - Manages smooth visual animations for bilateral stimulation
 *
 * Features:
 * - 60fps animations using requestAnimationFrame
 * - Multiple movement patterns (horizontal, vertical, diagonal, infinity)
 * - GPU-accelerated transforms
 * - Smooth, non-linear motion
 */
export class BLSAnimationEngine {
  private animationFrame: number | null = null;
  private startTime: number = 0;
  private speed: number = 5; // Hz (passes per second)
  private isRunning: boolean = false;
  private element: HTMLElement | null = null;
  private direction: Direction = 'horizontal';
  private movementStyle: MovementStyle = 'smooth';
  private containerWidth: number = window.innerWidth;
  private containerHeight: number = window.innerHeight;
  private circleSize: number = 96; // Circle diameter in pixels (default: 96px)
  private randomTargets: { x: number; y: number }[] = []; // For random movement
  private onPanUpdate?: (panValue: number) => void; // Callback for pan updates
  private trailPositions: Position[] = []; // Store recent positions for trail effect
  private onTrailUpdate?: (positions: Position[]) => void; // Callback for trail positions
  private onOpacityUpdate?: (opacity: number) => void; // Callback for pulse opacity

  constructor() {
    // Update container dimensions on resize
    window.addEventListener('resize', this.updateDimensions.bind(this));
    // Generate random targets
    this.generateRandomTargets();
  }

  /**
   * Generate random target positions for truly random movement
   */
  private generateRandomTargets(): void {
    const circleRadius = this.circleSize / 2;
    const baseAmplitude = 0.45; // 45% of container leaves ~5% padding from edges

    // Calculate safe amplitude for random targets accounting for circle radius
    const maxXMovement = (this.containerWidth * baseAmplitude) - circleRadius;
    const maxYMovement = (this.containerHeight * baseAmplitude) - circleRadius;

    const safeXAmplitude = Math.max(0, maxXMovement);
    const safeYAmplitude = Math.max(0, maxYMovement);

    this.randomTargets = [];

    // Generate 50 random target positions within safe bounds
    for (let i = 0; i < 50; i++) {
      this.randomTargets.push({
        x: (Math.random() - 0.5) * 2 * safeXAmplitude,
        y: (Math.random() - 0.5) * 2 * safeYAmplitude,
      });
    }
  }

  /**
   * Update container dimensions based on actual element container
   */
  private updateDimensions(): void {
    if (this.element && this.element.parentElement) {
      const parent = this.element.parentElement;
      this.containerWidth = parent.clientWidth;
      this.containerHeight = parent.clientHeight;
    } else {
      this.containerWidth = window.innerWidth;
      this.containerHeight = window.innerHeight;
    }

    // Regenerate random targets with new dimensions
    if (this.direction === 'random') {
      this.generateRandomTargets();
    }
  }

  /**
   * Calculate axis position based on movement style
   */
  private calculateAxisPosition(phase: number, amplitude: number): number {
    switch (this.movementStyle) {
      case 'smooth':
      case 'spread':
        // Both use sine wave - spread adds visual trail effect in rendering
        return Math.sin(phase) * amplitude;

      case 'blink':
        // Instant position changes (snap to extremes)
        const blinkPhase = phase % (2 * Math.PI);
        return blinkPhase < Math.PI ? amplitude : -amplitude;

      case 'pulse':
        // Stationary at extremes - teleport between sides
        // Phase 0 to π = right side (+amplitude), π to 2π = left side (-amplitude)
        const pulsePhase = phase % (2 * Math.PI);
        return pulsePhase < Math.PI ? amplitude : -amplitude;

      default:
        return Math.sin(phase) * amplitude;
    }
  }

  /**
   * Calculate pulse opacity based on phase
   * Each half-cycle (π radians):
   * - 0-20%: Fade IN (0 → 1)
   * - 20-70%: Hold at full opacity (1)
   * - 70-100%: Fade OUT (1 → 0)
   */
  private calculatePulseOpacity(phase: number): number {
    const fullPhase = phase % (2 * Math.PI);
    // Get progress within current half-cycle (0 to 1)
    const halfCycleProgress = fullPhase < Math.PI
      ? fullPhase / Math.PI
      : (fullPhase - Math.PI) / Math.PI;

    const fadeInEnd = 0.2;   // First 20% is fade in
    const holdEnd = 0.21;    // Hold at full opacity for 1%

    if (halfCycleProgress < fadeInEnd) {
      // Fade IN: 0 → 1 during first 20%
      return halfCycleProgress / fadeInEnd;
    } else if (halfCycleProgress < holdEnd) {
      // Hold at full opacity
      return 1;
    } else {
      // Fade OUT: 1 → 0 during last 30%
      const fadeOutProgress = (halfCycleProgress - holdEnd) / (1 - holdEnd);
      return 1 - fadeOutProgress;
    }
  }

  /**
   * Calculate position based on elapsed time and direction
   */
  private calculatePosition(elapsed: number, direction: Direction): Position {
    // Pulse mode runs at 1/3 speed to allow visible fade effect
    const pulseSpeedMultiplier = this.movementStyle === 'pulse' ? 0.33 : 1;
    const frequency = this.speed * pulseSpeedMultiplier; // Hz
    const phase = (elapsed * frequency * 2 * Math.PI) % (2 * Math.PI);

    // Calculate safe amplitude accounting for circle radius
    const circleRadius = this.circleSize / 2;
    const baseAmplitude = 0.45; // 45% of container leaves ~5% padding from edges

    // Calculate maximum safe movement for each axis
    const maxXMovement = (this.containerWidth * baseAmplitude) - circleRadius;
    const maxYMovement = (this.containerHeight * baseAmplitude) - circleRadius;

    // Ensure we don't exceed container bounds
    const safeXAmplitude = Math.max(0, maxXMovement);
    const safeYAmplitude = Math.max(0, maxYMovement);

    switch (direction) {
      case 'horizontal':
        return {
          x: this.calculateAxisPosition(phase, safeXAmplitude),
          y: 0,
        };

      case 'vertical':
        return {
          x: 0,
          y: this.calculateAxisPosition(phase, safeYAmplitude),
        };

      case 'diagonal':
        return {
          x: this.calculateAxisPosition(phase, safeXAmplitude),
          y: this.calculateAxisPosition(phase, safeYAmplitude),
        };

      case 'infinity':
        // Lemniscate (figure-8) pattern
        return {
          x: this.calculateAxisPosition(phase, safeXAmplitude),
          y: this.calculateAxisPosition(phase * 2, safeYAmplitude * 0.5),
        };

      case 'random':
        // Truly random movement using pre-generated targets
        const cycleTime = 1 / frequency; // Time for one cycle in seconds
        const targetIndex = Math.floor(elapsed / cycleTime) % this.randomTargets.length;
        const nextIndex = (targetIndex + 1) % this.randomTargets.length;

        // Smooth interpolation between targets
        const progress = (elapsed % cycleTime) / cycleTime;
        const eased = 0.5 - Math.cos(progress * Math.PI) / 2; // Ease in-out

        const current = this.randomTargets[targetIndex];
        const next = this.randomTargets[nextIndex];

        return {
          x: current.x + (next.x - current.x) * eased,
          y: current.y + (next.y - current.y) * eased,
        };

      default:
        return { x: 0, y: 0 };
    }
  }

  /**
   * Animation loop
   */
  private animate = (currentTime: number): void => {
    if (!this.isRunning || !this.element) return;

    const elapsed = (currentTime - this.startTime) / 1000; // Convert to seconds
    const position = this.calculatePosition(elapsed, this.direction);

    // Apply transform (GPU-accelerated) - background is handled by React component
    this.element.style.transform = `translate(${position.x}px, ${position.y}px)`;

    // Track positions for trail effect (spread and pulse modes)
    if (this.movementStyle === 'spread' || this.movementStyle === 'pulse') {
      this.trailPositions.unshift({ ...position });
      // Keep last 10 positions
      if (this.trailPositions.length > 10) {
        this.trailPositions.pop();
      }
      // Notify trail update callback
      if (this.onTrailUpdate) {
        this.onTrailUpdate([...this.trailPositions]);
      }
    } else {
      // Clear trail positions when not in spread/pulse mode
      if (this.trailPositions.length > 0) {
        this.trailPositions = [];
        if (this.onTrailUpdate) {
          this.onTrailUpdate([]);
        }
      }
    }

    // Calculate pan value for audio triggering (-1 to 1)
    // Use appropriate axis based on direction
    // IMPORTANT: Use same amplitude (0.45) as position calculation to ensure full range
    if (this.onPanUpdate) {
      let panValue = 0;

      // Calculate actual safe amplitudes (accounting for circle radius)
      const circleRadius = this.circleSize / 2;
      const baseAmplitude = 0.45;
      const maxXMovement = (this.containerWidth * baseAmplitude) - circleRadius;
      const maxYMovement = (this.containerHeight * baseAmplitude) - circleRadius;
      const safeXAmplitude = Math.max(1, maxXMovement); // Minimum 1 to avoid division by zero
      const safeYAmplitude = Math.max(1, maxYMovement);

      switch (this.direction) {
        case 'horizontal':
        case 'diagonal':
        case 'infinity':
          // Use horizontal position
          panValue = position.x / safeXAmplitude;
          break;

        case 'vertical':
          // Use vertical position for vertical movement
          panValue = position.y / safeYAmplitude;
          break;

        case 'random':
          // Use combined distance from center for random movement
          const xNorm = position.x / safeXAmplitude;
          const yNorm = position.y / safeYAmplitude;
          // Use the axis with greater displacement
          panValue = Math.abs(xNorm) > Math.abs(yNorm) ? xNorm : yNorm;
          break;
      }

      this.onPanUpdate(panValue);
    }

    // Calculate and report pulse opacity
    if (this.movementStyle === 'pulse' && this.onOpacityUpdate) {
      const pulseSpeedMultiplier = 0.33; // Match the multiplier in calculatePosition
      const frequency = this.speed * pulseSpeedMultiplier;
      const phase = (elapsed * frequency * 2 * Math.PI) % (2 * Math.PI);
      const opacity = this.calculatePulseOpacity(phase);
      this.onOpacityUpdate(opacity);
    }

    // Continue animation
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  /**
   * Start the animation
   */
  start(
    element: HTMLElement,
    config: {
      direction: Direction;
      speed: number;
      color: string;
      size: number; // Circle diameter in pixels
      movementStyle: MovementStyle;
      onPanUpdate?: (panValue: number) => void;
      onTrailUpdate?: (positions: Position[]) => void;
      onOpacityUpdate?: (opacity: number) => void;
    }
  ): void {
    if (this.isRunning) {
      this.stop();
    }

    this.element = element;
    this.direction = config.direction;
    this.speed = config.speed;
    this.circleSize = config.size; // Store circle size
    this.movementStyle = config.movementStyle;
    this.onPanUpdate = config.onPanUpdate;
    this.onTrailUpdate = config.onTrailUpdate;
    this.onOpacityUpdate = config.onOpacityUpdate;
    this.trailPositions = []; // Reset trail positions
    this.startTime = performance.now();
    this.isRunning = true;

    // Update dimensions in case window was resized
    this.updateDimensions();

    // Regenerate random targets if using random direction
    if (config.direction === 'random') {
      this.generateRandomTargets();
    }

    // Start animation loop
    this.animationFrame = requestAnimationFrame(this.animate);
  }

  /**
   * Stop the animation
   */
  stop(): void {
    this.isRunning = false;
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    // Reset position
    if (this.element) {
      this.element.style.transform = 'translate(0, 0)';
    }

    // Clear trail positions
    if (this.trailPositions.length > 0) {
      this.trailPositions = [];
      if (this.onTrailUpdate) {
        this.onTrailUpdate([]);
      }
    }
  }

  /**
   * Update animation speed
   */
  updateSpeed(speed: number): void {
    this.speed = speed;
  }

  /**
   * Update animation direction
   */
  updateDirection(direction: Direction): void {
    this.direction = direction;

    // Regenerate random targets if switching to random direction
    if (direction === 'random') {
      this.generateRandomTargets();
    }
  }

  /**
   * Update circle color (no-op, background handled by React component)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateColor(_color: string): void {
    // Color is now handled by React component's gradient background
  }

  /**
   * Update movement style
   */
  updateMovementStyle(movementStyle: MovementStyle): void {
    this.movementStyle = movementStyle;
  }

  /**
   * Update callbacks (for React hook updates)
   */
  updateCallbacks(
    onPanUpdate?: (panValue: number) => void,
    onTrailUpdate?: (positions: Position[]) => void,
    onOpacityUpdate?: (opacity: number) => void
  ): void {
    this.onPanUpdate = onPanUpdate;
    this.onTrailUpdate = onTrailUpdate;
    this.onOpacityUpdate = onOpacityUpdate;
  }

  /**
   * Check if animation is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }
}

// Create singleton instance
let animationEngineInstance: BLSAnimationEngine | null = null;

/**
 * Get the singleton animation engine instance
 */
export function getAnimationEngine(): BLSAnimationEngine {
  if (!animationEngineInstance) {
    animationEngineInstance = new BLSAnimationEngine();
  }
  return animationEngineInstance;
}
